import { GetLessonBookingGreetingString } from 'app/components/call/LessonBookingGreeting';
import { stripTitleMarkup } from 'app/components/common/FormattedText/strip';
import { useI18n, tFunctionType } from 'app/components/localization';
import { assetBaseURL } from 'app/config/client';
import { getGlobalHistory } from 'app/services/history';
import { SetHead, ITagDescriptions } from 'app/utils/HeadContext';
import { AlternateTagDesciptions, ICanonicalTagDescription } from 'app/utils/HeadContext/common';
import { getLegacyNodeDataForManifestContent } from 'app/utils/manifest';
import { getFullUrl } from 'app/utils/routes';
import React, { useEffect } from 'react';
import { useBrand, BrandContextValue } from '../OrganizationContext';
import { List } from 'immutable';
import { Format, CropMode } from '@engoo/transcoder-service';
import { useTranscoderService } from 'app/services/transcoderService/react';
import { BrandLogo } from 'app/constants/schemas';
import { MaybeGetEntity } from 'app/containers/entities/GetEntity';
import { getWindow } from 'app/utils/env';
import { useDisplayLanguage } from 'app/components/LanguageContext';

export const MetaImageUrlContext = React.createContext<string | undefined>(undefined);

export interface IPageMetaProps {
  title?: string;
  description?: string;
  noIndex?: boolean;
  canonicalTagDescriptions?: List<ICanonicalTagDescription>;
  alternateTagDescriptions?: AlternateTagDesciptions;
}

interface IPropsWithDeps extends IPageMetaProps {
  brand: BrandContextValue;
  faviconUrl?: string;
  metaImageUrl?: string;
  lessonGreeting: string | null;
  t: tFunctionType;
}

/**
 * Sets the title/description and other page meta-data, but without actually rendering anything.
 *
 * In most cases, you should use <Page> instead of using <PageMeta> directly.
 */
const PageMetaInjectDependencies: React.FunctionComponent<IPageMetaProps> = ({
  title,
  description,
  noIndex,
  canonicalTagDescriptions,
  children,
  alternateTagDescriptions,
}) => {
  const brand = useBrand();
  const { t } = useI18n();

  const displayLanguage = useDisplayLanguage();

  // This only addresses client side changes for updating the display language.
  // Server side has to be handled when generating the index file.
  useEffect(() => {
    const w = getWindow();
    if (w) {
      w.document.documentElement.lang = displayLanguage.id;
    }
  }, [displayLanguage]);

  return (
    <MetaImageUrlContext.Consumer>
      {metaImageUrl => (
        <GetLessonBookingGreetingString>
          {lessonGreeting => (
            <MaybeGetEntity type={BrandLogo} id={brand.squareLogo}>
              {squareLogo => (
                <FaviconTranscoder src={squareLogo?.url}>
                  {transcodedLogoUrl => (
                    <SetHead
                      tags={getHeadTags({
                        brand,
                        title,
                        description,
                        metaImageUrl,
                        canonicalTagDescriptions,
                        t,
                        lessonGreeting,
                        // For the time being, do not index third party pages
                        noIndex: noIndex || !brand.isFirstParty,
                        alternateTagDescriptions,
                        faviconUrl: transcodedLogoUrl ?? FAVICON_PATH,
                      })}
                    >
                      {children}
                    </SetHead>
                  )}
                </FaviconTranscoder>
              )}
            </MaybeGetEntity>
          )}
        </GetLessonBookingGreetingString>
      )}
    </MetaImageUrlContext.Consumer>
  );
};

const MOBILE_ICONS = {
  manifestPath: '/manifests/manifest.json',
  iconsPath: '/images/mobileIcons',
};
const FAVICON_PATH = `${assetBaseURL}/images/meta/favicon.ico`;

/**
 * Sets the meta data based on the page properties.
 *
 * @param {Object} props
 */
export function getHeadTags(props: IPropsWithDeps): ITagDescriptions {
  const {
    description,
    noIndex,
    canonicalTagDescriptions,
    metaImageUrl,
    t,
    brand,
    alternateTagDescriptions,
    faviconUrl,
  } = props;

  const descriptionWithFallback = description || t('page.brand.description', { brand: brand.name });

  return {
    title: { tag: 'title', attributes: { children: getTitle(props) } },
    'og:title': { tag: 'meta', attributes: { property: 'og:title', content: getTitle(props) } },
    description: {
      tag: 'meta',
      attributes: { name: 'description', content: descriptionWithFallback },
    },
    'og:description': {
      tag: 'meta',
      attributes: { property: 'og:description', content: descriptionWithFallback },
    },
    'og:type': { tag: 'meta', attributes: { property: 'og:type', content: 'website' } },
    'og:url': {
      tag: 'meta',
      attributes: { property: 'og:url', content: getFullUrl(getGlobalHistory().location) },
    },
    'og:image': {
      tag: 'meta',
      attributes: {
        property: 'og:image',
        // NOTE: og:image must be an absolute URL
        content: (
          metaImageUrl ||
          getGlobalHistory().getAbsoluteUrl(
            // Use largest available mobile icon as the default Open Graph image.
            `${assetBaseURL}${MOBILE_ICONS.iconsPath}/192x192.png`,
          )
        ),
      },
    },
    noIndex: noIndex ? {
      tag: 'meta',
      attributes: { name: 'robots', content: 'noindex' },
    } : undefined,
    ...canonicalTagDescriptions?.toJS(),
    icon: { tag: 'link', attributes: { rel: 'icon', href: faviconUrl } },
    ...alternateTagDescriptions,
    ...getMobileIconTags({ brand }),
  };
}

function getTitle(props: IPropsWithDeps): string {
  const { title, lessonGreeting, brand } = props;

  const baseTitle = title || brand.name;

  const result = lessonGreeting ? `${lessonGreeting} | ${baseTitle}` : baseTitle;

  return stripTitleMarkup(result);
}

function getMobileIconTags({ brand }: { brand: BrandContextValue }): ITagDescriptions {
  const { manifestPath, iconsPath } = MOBILE_ICONS;
  const result: ITagDescriptions = {
    manifest: {
      tag: 'link',
      attributes: { rel: 'manifest', href: `${assetBaseURL}${manifestPath}` },
    },
  };

  // Inject mobile icons.
  getLegacyNodeDataForManifestContent(`${assetBaseURL}${iconsPath}`)
    .forEach((attributes, index) => {
      result[`mobile-icon-${index}`] = { tag: 'link', attributes };
    });

  return result;
}

/**
 * Provides a version of an image specified by `src` in the favicon format.
 */
const FaviconTranscoder: React.FunctionComponent<{
  /**
   * An image url to transcode to favicon format
   */
  src?: string;
  children: (faviconUrl?: string) => React.ReactElement;
}> = ({ src, children }) => {
  const transcoder = useTranscoderService();
  // This ESLint rule is buggy
  /* eslint-disable indent-legacy */
  const faviconUrl =
    src && transcoder
      ? transcoder.getTranscodedSrc(src, {
          format: Format.Png,
          height: 48,
          width: 48,
          cropMode: CropMode.Pad,
        })
      : undefined;
  return children(faviconUrl);
};

export default PageMetaInjectDependencies;
